"use client";

/**
 * Last-resort error boundary — catches crashes in the ROOT LAYOUT itself,
 * where app/error.tsx cannot help. Must render its own <html>/<body>.
 * Same recovery logic: stale-deploy chunk failures reload once silently,
 * everything else gets a styled retry screen and a telemetry event.
 */

import { useEffect, useState } from "react";
import { maybeAutoReload, reportCrash } from "@/lib/crash-recovery";

export default function GlobalError({
  error,
}: {
  error: Error & { digest?: string };
}) {
  const [reloading, setReloading] = useState(false);

  useEffect(() => {
    const reloaded = maybeAutoReload(error);
    setReloading(reloaded);
    reportCrash(error, { boundary: "global", digest: error?.digest, autoReloading: reloaded });
  }, [error]);

  return (
    <html lang="en">
      <body style={{ margin: 0, background: "#09090b" }}>
        {reloading ? null : (
          <div
            style={{
              minHeight: "100vh",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "24px",
              fontFamily:
                "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
            }}
          >
            <div
              style={{
                maxWidth: "420px",
                width: "100%",
                textAlign: "center",
                border: "1px solid rgba(255,255,255,0.1)",
                borderRadius: "16px",
                background: "rgba(24,24,27,0.8)",
                padding: "32px",
              }}
            >
              <h1 style={{ color: "#fff", fontSize: "18px", margin: 0 }}>
                Something went wrong
              </h1>
              <p style={{ color: "rgba(255,255,255,0.6)", fontSize: "14px", marginTop: "8px" }}>
                A quick refresh usually fixes this. Your work is saved.
              </p>
              <button
                type="button"
                onClick={() => window.location.reload()}
                style={{
                  marginTop: "24px",
                  background: "#fff",
                  color: "#09090b",
                  border: "none",
                  borderRadius: "8px",
                  padding: "8px 16px",
                  fontSize: "14px",
                  fontWeight: 500,
                  cursor: "pointer",
                }}
              >
                Refresh page
              </button>
            </div>
          </div>
        )}
      </body>
    </html>
  );
}
